import React, {useState, useEffect, useCallback, useMemo, useContext} from "react";

import {
    AuthService,
    CartService,
    NotificationService,
    UserService,
    UtilService,
} from "../services";
import {msalInstance, normalizeRegionConfig, useErrorContext} from "tds_shared_ui";

const Context = React.createContext({
    user: null,
    setUser: () => {},
    userAgreed: false,
    setUserAgreed: () => {},
    cart: [],
    cartSubTotal: "",
    cartQuantity: "",
    notificationData: [],
    addToCart: () => {},
    removeFromCart: () => {},
    displayCart: false,
    displayNotification: false,
    dismissNotification: () => {},
    setNotificationDisplay: () => {},
    setCartDisplay: () => {},
    emptyCart: () => {},
    updateCart: () => {},
    saveCart: () => {},
});

export const UserProvider = (props) => {
    const [user, setUserState] = useState(null);
    const [cart, setCart] = useState([]);
    const [userAgreed, setUserAgreed] = useState(false);
    const [cartSubTotal, setCartSubTotal] = useState("");
    const [cartQuantity, setCartQuantity] = useState("");
    const [notificationData, setNotificationData] = useState([]);
    const [displayCart, setDisplayCart] = useState(false);
    const [displayNotification, setDisplayNotification] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);

    const { displayError } = useErrorContext();
    const normalizedRegionConfig = useMemo(() => {
        if (props.regionData?.regionConfigs) {
            return normalizeRegionConfig(props.regionData.regionConfigs);
        }
        return null;
    }, [props.regionData?.regionConfigs]);

    useEffect(() => {
        const handleMsalLogin = async () => {
            try {
                await msalInstance.initialize();
                if (!msalInstance.getActiveAccount()) {
                    const [account] = msalInstance.getAllAccounts();
                    if (account) {
                        msalInstance.setActiveAccount(account);
                    }
                }
                const isLoggingIn = await AuthService.authenticate({
                    setIsAuthenticating,
                    setUser: (user) => {
                        setUser(user);
                        setUserAgreed(false); // Always require agreement on new session
                    },
                    displayError
                });

                const activeAccount = msalInstance.getActiveAccount();
                if (!isLoggingIn && activeAccount) {
                    setIsAuthenticating(true);
                    UserService.getUser()
                        .then((user) => {
                            setUser(user)
                        })
                        .catch((e) => {
                            console.error("Failed to get user: ", e);
                            setUser(null);
                            msalInstance.setActiveAccount(null);
                            displayError(e);
                        })
                        .finally(() => {
                            setIsAuthenticating(false);
                        });
                }
            } catch (e) {
                console.error("Failed to initialize MSAL or authenticate:", e);
                displayError(e);
                setIsAuthenticating(false);
            }
        }

        handleMsalLogin();
    }, []);

    useEffect(() => {
        const agreed = localStorage.getItem('userAgreed') === 'true';
        setUserAgreed(agreed);
    }, []);

    useEffect(() => {
        localStorage.setItem('userAgreed', userAgreed);
    }, [userAgreed]);

    const showCompleteProfileNotification = useCallback(() => {
        return !!(
            user &&
            (!user.firstName ||
                !user.lastName ||
                !user.address ||
                (normalizedRegionConfig?.features?.requireVAT && !user.vat))
        );
    }, [user, normalizedRegionConfig]);

    const fetchIncompleteProfileNotification = useCallback(() => {
        NotificationService.getIncompleteProfileNotification({
            headers: { "Accept-Language": props.locale.replace("_", "-") },
        })
            .then((data) => {
                if (!Array.isArray(data)) return;
                setNotificationData((prev) => [
                    ...prev,
                    ...data.filter((item) => !prev.some((n) => n.id === item.id))
                ]);
            })
            .catch((e) => {
                console.error("Failed to fetch incomplete profile notification:", e);
                displayError(e);
            });
    } , []);

    useEffect(() => {
        if (showCompleteProfileNotification()) {
            fetchIncompleteProfileNotification();
        }
    }, [showCompleteProfileNotification, fetchIncompleteProfileNotification]);

    const fetchNotifications = useCallback(() => {
        NotificationService.getNotifications({
            headers: { "Accept-Language": props.locale.replace("_", "-") },
        })
            .then((d) => {
                if (!Array.isArray(d)) return;
                setNotificationData((prev) => [
                    ...prev,
                    ...d.filter((item) => !prev.some((n) => n.id === item.id))
                ]);
            })
            .catch((e) => {
                console.error("Failed to fetch notifications:", e);
                displayError(e);
            });
    }, [props.locale, showCompleteProfileNotification]);

    const setUser = useCallback(
        (user) => {
            setUserState(user);
            setCart([]);
            setNotificationData([]);
            fetchNotifications();
            if(user) {
                props.onLocaleChange(user.preferredLanguage);
                if(normalizedRegionConfig?.features?.cartAvailable) {
                    CartService.getCart()
                        .then((data) => {
                            setCart(data.items || []);
                            setCartSubTotal(data.subTotal || "");
                            setCartQuantity(data.quantityTotal || "");
                        })
                        .catch((e) => {
                            console.error("Failed to fetch cart data:", e);
                            displayError(e);
                        });
                }
            }
        },
        [props, fetchNotifications, normalizedRegionConfig]
    );

    const setNotificationDisplay = useCallback((bool) => {
        setDisplayNotification(bool);
    }, []);

    const dismissNotification = useCallback(
        (notification) => {
            setNotificationData((prev) =>
                prev.map((item) =>
                    item.id === notification.id ? { ...item, dismissed: true } : item
                )
            );
        },
        []
    );

    const addToCart = useCallback(
        (item) => {
            try {
                let newCart = cart.concat(item);
                return CartService.updateCart(newCart).then((data) => {
                    setCart(data.items);
                    setCartSubTotal(data.subTotal);
                    setCartQuantity(data.quantityTotal);
                });
            } catch (e) {
                console.error("Failed to add item to cart:", e);
                displayError(e);
            }
        },
        [cart]
    );

    const removeFromCart = useCallback(
        (skuid) => {
            let newCart = cart.filter((item) => item.skuid !== skuid);

            return CartService.updateCart(newCart)
                .then((data) => {
                    setCart(data.items);
                    setCartSubTotal(data.subTotal);
                    setCartQuantity(data.quantityTotal);
                    return data.items;
                })
                .catch((e) => {
                    console.error("Failed to remove item from cart:", e);
                    displayError(e);
                });
        },
        [cart]
    );

    const updateCart = useCallback(
        (updateItem) => {
            let newCart = cart.slice();
            for (const element of newCart) {
                let item = element;
                if (item.skuid === updateItem.skuid) {
                    item.quantity = updateItem.quantity;
                    break;
                }
            }
            setCart([...newCart]);
        },
        [cart]
    );

    const saveCart = useCallback(
        (cartArg) => {
            return CartService.updateCart(cartArg || cart)
                .then((data) => {
                    setCart(data.items);
                    setCartSubTotal(data.subTotal);
                    setCartQuantity(data.quantityTotal);
                    return data.items;
                })
                .catch((e) => {
                    console.error("Failed to save cart:", e);
                    displayError(e);
                });
        },
        [cart]
    );

    const emptyCart = useCallback(() => {
        return CartService.updateCart([])
            .then((data) => {
                setCart(data.items);
                setCartSubTotal(data.subTotal);
                setCartQuantity(data.quantityTotal);
                return data.items;
            })
            .catch((e) => {
                console.error("Failed to empty cart:", e);
                displayError(e);
            });
    }, []);

    const setCartDisplay = useCallback((bool) => {
        setDisplayCart(bool);
    }, []);

    useEffect(() => {
        if (!user) {
            fetchNotifications();
            return;
        }
        const { preferredLanguage } = user;
        const lang = props.locale.split(/[-_]+/)[0];
        if (lang !== preferredLanguage) {
            UtilService.setLanguage(lang)
                .then((d) => {
                    setUser(d);
                })
                .catch((e) => {
                    console.error("Failed to set language:", e);
                    displayError(e);
                });
        }
    }, [props.locale]);

    const contextValue = useMemo(
        () => ({
            user,
            setUser,
            userAgreed,
            setUserAgreed,
            cart,
            cartSubTotal,
            cartQuantity,
            notificationData,
            addToCart,
            removeFromCart,
            displayCart,
            displayNotification,
            dismissNotification,
            setNotificationDisplay,
            setCartDisplay,
            emptyCart,
            updateCart,
            saveCart,
            isAuthenticating,
        }),
        [
            user,
            setUser,
            userAgreed,
            setUserAgreed,
            cart,
            cartSubTotal,
            cartQuantity,
            notificationData,
            addToCart,
            removeFromCart,
            displayCart,
            displayNotification,
            dismissNotification,
            setNotificationDisplay,
            setCartDisplay,
            emptyCart,
            updateCart,
            saveCart,
            isAuthenticating,
        ]
    );

    return (
        <Context.Provider value={contextValue}>
            {props.children}
        </Context.Provider>
    );
};

export const UserConsumer = Context.Consumer;
export const useUserContext = () => useContext(Context);